// An important order leaves a mark (Nick, 14.10): a ripple on the button, a trail of light that draws itself from the button
// to the star on the map, and a lock-on on that star when it arrives (GalaxyMap.ack). A button opts in with
// `data-fx-target="<system id>"` (one delegated listener, no change to its own handler). One 2D canvas over the page, alive
// only while a trail runs. « Très bas »: no glow, shorter; reduced motion: nothing.
import { fxLevel, type FxLevel } from '../map/signal.js';

export interface FxMap { screenOf(id: string): { x: number; y: number } | null; ack(id: string): void }

let mapRef: (() => FxMap | null) | null = null;
let canvas: HTMLCanvasElement | null = null;
let raf = 0;
interface Trail { x0: number; y0: number; target: string; t0: number; arrived: boolean; last: { x: number; y: number } | null; fixed?: { x: number; y: number }; drawMs?: number }
const trails: Trail[] = [];
const ripples: { x: number; y: number; t0: number }[] = [];
let level: FxLevel = 'full';

/** The trail's shape: a bezier that bows away from the straight line (so it reads as a throw, not a ruler). */
export function trailPoint(x0: number, y0: number, x1: number, y1: number, u: number): { x: number; y: number } {
  const mx = (x0 + x1) / 2, my = (y0 + y1) / 2, dx = x1 - x0, dy = y1 - y0;
  const cx = mx + dy * 0.22, cy = my - Math.abs(dx) * 0.18 - 40;
  const a = (1 - u) * (1 - u), b = 2 * (1 - u) * u, c = u * u;
  return { x: a * x0 + b * cx + c * x1, y: a * y0 + b * cy + c * y1 };
}

const DRAW_MS = 760, FADE_MS = 650, RIPPLE_MS = 700;
/** The trail starts this long after the click (the ripple leads). */
const LEAD_MS = 120;

/** Trail to a fixed point of the page (a build slot, the destination label of a full-screen page): the trail ends ON the
 *  point, with a ring on arrival. Returns the delay in ms the caller should hold its transition so the gesture is seen
 *  (0 when effects are off: no trail, no delay). `drawMs` shortens the draw so it lands within the hold. */
export function decisionToPoint(from: Element, to: { x: number; y: number } | null, drawMs = DRAW_MS): number {
  if (level === 'off' || typeof document === 'undefined') return 0;
  const r = from.getBoundingClientRect();
  const x0 = r.left + r.width / 2, y0 = r.top + r.height / 2, now = performance.now();
  ripples.push({ x: x0, y: y0, t0: now });
  if (to) trails.push({ x0, y0, target: '', t0: now + LEAD_MS, arrived: false, last: to, fixed: to, drawMs });
  if (!raf) raf = requestAnimationFrame(frame);
  return to ? LEAD_MS + (level === 'lite' ? drawMs * 0.7 : drawMs) : 0;
}

/** Wires the delegated listener once; `getMap` gives the galaxy map of the moment (null when it is not on screen). */
export function installDecisionFx(getMap: () => FxMap | null): void {
  mapRef = getMap;
  if (typeof document === 'undefined' || (document as unknown as { __fxWired?: boolean }).__fxWired) return;
  (document as unknown as { __fxWired?: boolean }).__fxWired = true;
  level = fxLevel();
  document.addEventListener('click', (e) => {
    const el = (e.target as Element | null)?.closest?.('[data-fx-target]') as HTMLElement | null;
    if (!el || (el as HTMLButtonElement).disabled) return;
    const target = el.dataset.fxTarget;
    if (target) decision(el, target);
  }, true);
}

/** Plays the effect from an element (or a point) to a star. */
export function decision(from: Element | { x: number; y: number }, target: string): void {
  if (level === 'off' || typeof document === 'undefined') return;
  const r = 'getBoundingClientRect' in from ? from.getBoundingClientRect() : null;
  const x0 = r ? r.left + r.width / 2 : (from as { x: number }).x, y0 = r ? r.top + r.height / 2 : (from as { y: number }).y;
  if (r) { (from as HTMLElement).classList.remove('fx-pressed'); void (from as HTMLElement).offsetWidth; (from as HTMLElement).classList.add('fx-pressed'); setTimeout(() => (from as HTMLElement).classList.remove('fx-pressed'), 900); }
  const now = performance.now();
  ripples.push({ x: x0, y: y0, t0: now });
  // the trail only when the star is in sight (not under a full-screen page such as the preparation)
  const to = mapRef?.()?.screenOf(target) ?? null;
  const seen = to && typeof document.elementFromPoint === 'function' ? document.elementFromPoint(to.x, to.y)?.tagName === 'CANVAS' : !!to;
  if (seen) trails.push({ x0, y0, target, t0: now + LEAD_MS, arrived: false, last: null });
  if (!raf) raf = requestAnimationFrame(frame);
}

function ensureCanvas(): CanvasRenderingContext2D | null {
  if (!canvas) {
    canvas = document.createElement('canvas');
    canvas.className = 'decision-fx';
    canvas.setAttribute('aria-hidden', 'true');
    canvas.style.cssText = 'position:fixed;inset:0;width:100vw;height:100vh;pointer-events:none;z-index:60';
    document.body.appendChild(canvas);
  }
  const dpr = Math.min(2, window.devicePixelRatio || 1), w = Math.round(window.innerWidth * dpr), h = Math.round(window.innerHeight * dpr);
  if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
  const ctx = canvas.getContext('2d');
  ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
}

function frame(): void {
  raf = 0;
  const ctx = ensureCanvas();
  if (!ctx) return;
  ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
  const now = performance.now(), glow = level === 'full';
  for (const rp of ripples.slice()) {
    const u = (now - rp.t0) / RIPPLE_MS;
    if (u >= 1) { ripples.splice(ripples.indexOf(rp), 1); continue; }
    const e = 1 - (1 - u) ** 3;
    ctx.strokeStyle = `rgba(255, 209, 102, ${0.9 * (1 - u)})`; ctx.lineWidth = 3 * (1 - u) + 1;
    ctx.beginPath(); ctx.arc(rp.x, rp.y, 10 + 70 * e, 0, Math.PI * 2); ctx.stroke();
    if (glow) { ctx.strokeStyle = `rgba(125, 211, 252, ${0.6 * (1 - u)})`; ctx.beginPath(); ctx.arc(rp.x, rp.y, 6 + 110 * e, 0, Math.PI * 2); ctx.stroke(); }
  }
  const map = mapRef?.() ?? null;
  for (const tr of trails.slice()) {
    const el = now - tr.t0;
    if (el < 0) continue;
    const to = tr.fixed ?? map?.screenOf(tr.target) ?? tr.last;
    if (!to) { trails.splice(trails.indexOf(tr), 1); continue; }
    tr.last = to;
    const base = tr.drawMs ?? DRAW_MS, draw = level === 'lite' ? base * 0.7 : base;
    const u1 = Math.min(1, el / draw), e1 = u1 < 0.5 ? 2 * u1 * u1 : 1 - (-2 * u1 + 2) ** 2 / 2;
    const u0 = el > draw ? Math.min(1, (el - draw) / FADE_MS) : 0;
    if (u0 >= 1) { trails.splice(trails.indexOf(tr), 1); continue; }
    if (!tr.arrived && u1 >= 1) { tr.arrived = true; if (tr.fixed) ripples.push({ x: to.x, y: to.y, t0: now }); else map?.ack(tr.target); }
    const steps = 48;
    ctx.lineCap = 'round';
    for (const [w, col, blur] of (glow ? [[9, 'rgba(125, 211, 252, 0.28)', 18], [3, 'rgba(236, 250, 255, 0.95)', 10]] : [[3, 'rgba(200, 240, 255, 0.9)', 0]]) as [number, string, number][]) {
      ctx.strokeStyle = col; ctx.lineWidth = w; ctx.shadowColor = 'rgba(125, 211, 252, 0.9)'; ctx.shadowBlur = blur;
      ctx.beginPath();
      for (let i = 0; i <= steps; i++) {
        const u = u0 * e1 + (e1 - u0 * e1) * (i / steps);
        const p = trailPoint(tr.x0, tr.y0, to.x, to.y, u);
        if (i === 0) ctx.moveTo(p.x, p.y); else ctx.lineTo(p.x, p.y);
      }
      ctx.globalAlpha = 1 - u0;
      ctx.stroke();
    }
    ctx.globalAlpha = 1; ctx.shadowBlur = 0;
    if (u1 < 1) {
      const h = trailPoint(tr.x0, tr.y0, to.x, to.y, e1);
      const g = ctx.createRadialGradient(h.x, h.y, 0, h.x, h.y, glow ? 22 : 10);
      g.addColorStop(0, 'rgba(255,255,255,1)'); g.addColorStop(0.35, 'rgba(160,230,255,0.85)'); g.addColorStop(1, 'rgba(125,211,252,0)');
      ctx.fillStyle = g; ctx.beginPath(); ctx.arc(h.x, h.y, glow ? 22 : 10, 0, Math.PI * 2); ctx.fill();
    }
  }
  if (trails.length || ripples.length) raf = requestAnimationFrame(frame);
  else ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
}
